/**
 * @fileoverview Privacy Policy page
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Shield, Eye, Lock, Users, Globe, Calendar, AlertTriangle, CheckCircle } from 'lucide-react';
import { HelpPageLayout } from '../../components/help/HelpPageLayout';

const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();

  const lastUpdated = "January 15, 2025";

  const sections = [
    {
      icon: <Eye className="w-6 h-6" />,
      title: "Information We Collect",
      content: [
        {
          subtitle: "Information you provide:",
          items: [
            "Account information (email, name) when you create an account",
            "Game preferences and settings",
            "User-generated content (custom spinners)",
            "Feedback and support messages"
          ]
        },
        {
          subtitle: "Information collected automatically:",
          items: [
            "Device information (browser type, operating system)",
            "Usage data (pages visited, features used)",
            "IP address and general location",
            "Cookies and similar technologies"
          ]
        }
      ]
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "How We Use Your Information",
      content: [
        {
          subtitle: "To provide our services:",
          items: [
            "Create and manage your account",
            "Provide personalized game experiences",
            "Save your preferences and game history",
            "Enable social features and sharing"
          ]
        },
        {
          subtitle: "To improve our services:",
          items: [
            "Analyze usage patterns and trends",
            "Develop new features and content",
            "Fix bugs and technical issues",
            "Optimize performance and user experience"
          ]
        },
        {
          subtitle: "To communicate with you:",
          items: [
            "Send important service updates",
            "Respond to your support requests",
            "Send marketing communications (with consent)",
            "Notify you of new features"
          ]
        }
      ]
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Information Sharing",
      content: [
        {
          subtitle: "We do not sell your personal information.",
          items: [
            "We may share data with service providers who help us operate our platform",
            "We may share information if required by law or to protect our rights",
            "We may share aggregated, anonymized data for analytics purposes",
            "Your shared spinners may be visible to other users (based on your settings)"
          ]
        }
      ]
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Data Security",
      content: [
        {
          subtitle: "We implement appropriate security measures:",
          items: [
            "Encryption of data in transit and at rest",
            "Regular security audits and updates",
            "Access controls and authentication",
            "Secure hosting and infrastructure"
          ]
        }
      ]
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Data Retention",
      content: [
        {
          subtitle: "We retain your information for:",
          items: [
            "Account data: Until you delete your account",
            "Usage data: Up to 2 years for analytics",
            "Game history: Until you delete your account",
            "Support communications: Up to 3 years"
          ]
        }
      ]
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Your Rights",
      content: [
        {
          subtitle: "You have the right to:",
          items: [
            "Access your personal information",
            "Correct inaccurate data",
            "Delete your account and data",
            "Export your data",
            "Opt out of marketing communications",
            "Object to certain processing activities"
          ]
        }
      ]
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "GDPR Compliance (EEA, UK, Switzerland)",
      content: [
        {
          subtitle: "For users in the European Economic Area (EEA), United Kingdom, and Switzerland:",
          items: [
            "We process your data based on legitimate interests and consent",
            "You have the right to withdraw consent at any time",
            "You can request data portability and erasure",
            "We implement appropriate data protection measures",
            "You can lodge complaints with your local data protection authority"
          ]
        },
        {
          subtitle: "Cookie Consent:",
          items: [
            "We use a GDPR-compliant consent management system",
            "Essential cookies are required for basic functionality",
            "Analytics and advertising cookies require explicit consent",
            "You can customize your cookie preferences at any time",
            "Consent can be withdrawn through our privacy settings"
          ]
        }
      ]
    }
  ];

  const cookies = [
    {
      name: "Essential Cookies",
      purpose: "Required for basic functionality",
      duration: "Session or 1 year",
      examples: "Authentication, session management, security"
    },
    {
      name: "Analytics Cookies",
      purpose: "Help us understand how users interact with our site",
      duration: "Up to 2 years",
      examples: "Google Analytics, usage tracking"
    },
    {
      name: "Preference Cookies",
      purpose: "Remember your settings and preferences",
      duration: "Up to 1 year",
      examples: "Language settings, theme preferences, game settings"
    }
  ];

  return (
    <HelpPageLayout>
      <Helmet>
        <title>Privacy Policy - Spinner Games !!</title>
        <meta name="description" content="Learn how we collect, use, and protect your personal information when you use our spinner app." />
                  <meta property="og:title" content="Privacy Policy - Spinner Games !!" />
        <meta property="og:description" content="Your privacy is important to us. Learn how we protect your data." />
        <link rel="canonical" href="https://spinnergames.fun/help/privacy-policy" />
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Privacy Policy",
          "description": "Privacy policy for Spinner Games !!",
          "dateModified": lastUpdated,
          "publisher": {
            "@type": "Organization",
            "name": "Spinner Games !!"
          }
        })}
        </script>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-4">
            Your privacy is important to us
          </p>
          <p className="text-gray-500 dark:text-gray-400">
            Last updated: {lastUpdated}
          </p>
        </div>

        {/* Introduction */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-8">
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-4">
            At Spinner Games !!, we are committed to protecting your privacy and ensuring the security of your personal information. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our interactive 
            spinner application and related services.
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300">
            By using our service, you agree to the collection and use of information in accordance with this policy. 
            If you have any questions about this Privacy Policy, please contact us.
          </p>
        </div>

        {/* Main sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-300 mr-4">
                  {section.icon}
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {section.title}
                </h2>
              </div>
              
              <div className="space-y-6">
                {section.content.map((content, contentIndex) => (
                  <div key={contentIndex}>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      {content.subtitle}
                    </h3>
                    <ul className="space-y-2">
                      {content.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start text-gray-600 dark:text-gray-300">
                          <span className="text-blue-500 mr-2">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Cookies Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Cookies and Tracking Technologies
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            We use cookies and similar tracking technologies to enhance your experience and collect information about how you use our service.
          </p>
          
          <div className="space-y-4">
            {cookies.map((cookie, index) => (
              <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  {cookie.name}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-2">
                  <strong>Purpose:</strong> {cookie.purpose}
                </p>
                <p className="text-gray-600 dark:text-gray-300 mb-2">
                  <strong>Duration:</strong> {cookie.duration}
                </p>
                <p className="text-gray-600 dark:text-gray-300">
                  <strong>Examples:</strong> {cookie.examples}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Children's Privacy */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Children's Privacy
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Our service is designed to be family-friendly and safe for users of all ages. We do not knowingly collect personal 
            information from children under 13 without parental consent. If you are a parent or guardian and believe your child 
            has provided us with personal information, please contact us immediately.
          </p>
        </div>

        {/* International Users */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            International Users
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Our service is available worldwide. If you are accessing our service from outside your country of residence, 
            please note that your information may be transferred to and processed in countries with different privacy laws.
          </p>
        </div>

        {/* Changes to Policy */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Changes to This Privacy Policy
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new 
            Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy 
            Policy periodically for any changes.
          </p>
        </div>

        {/* Contact Information */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-8 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Contact Us
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <div className="space-y-2 text-gray-600 dark:text-gray-300">
            <p>Email: privacy@spinnergames.fun</p>
            <p>Contact Form: <a href="/help/contact" className="text-blue-600 dark:text-blue-400 hover:underline">Contact Us</a></p>
          </div>
        </div>

        {/* Back to Help */}
        <div className="text-center mt-8">
          <a
            href="/help"
            className="inline-flex items-center px-6 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors"
          >
            Back to Help Center
          </a>
        </div>
      </div>
    </HelpPageLayout>
  );
};

export default PrivacyPolicy; 